import { useMemo, useState } from "react";
import { Button, DialogActions, DialogContent, Divider, FormControlLabel, Grid, InputAdornment, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import useAppGlobal from "src/context/useAppGlobal";
import BrandUpdate from "./BrandUpdate";
import convertCurrency from "src/helper/convertCurrency";
import useCustomerUpdate from "src/hooks/user/customer/useCustomerUpdate";
import { customerType, userType } from "src/constant";
import { NumericFormat } from "react-number-format";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomerInput from "./CustomerInput";
import CustomerUpdateAutocomplete from "./CustomerAutocomplete";
import CustomerUpdateAddress from "./CustomerUpdateAddress";
import * as yup from "yup";
import PhoneNumberInput from "src/components/shared/PhoneNumberInput";
import useDebounce from "src/hooks/useDebounce";
import useInfiniteAutocomplete from "src/hooks/useInfiniteAutocomplete";
import useCustomerCreate from "src/hooks/user/customer/useCustomerCreate";

const schema = yup.object().shape({
  fullName: yup.string().required("Vui lòng nhập tên đầy đủ"),
  email: yup.string().email("Email không hợp lệ").required("Vui lòng nhập email"),
  phone: yup.string().required("Vui lòng nhập số điện thoại"),
  debtLimit: yup.number().typeError("Giới hạn công nợ phải là số").min(0, "Giới hạn công nợ không được âm").required("Vui lòng nhập giới hạn công nợ"),
  type: yup.string().required("Vui lòng chọn loại khách hàng"),
  taxCode: yup.string().when("type", {
    is: (type) => type === customerType.business,
    then: (schema) => schema.required("Vui lòng nhập mã số thuế"),
    otherwise: (schema) => schema.notRequired(),
  }),
  companyName: yup.string().when("type", {
    is: (type) => type === customerType.business,
    then: (schema) => schema.required("Vui lòng nhập tên công ty"),
    otherwise: (schema) => schema.notRequired(),
  }),
  sale: yup.object().nullable().required("Vui lòng chọn nhân viên phụ trách"),
});

const brandTemp = {
  discount: 0,
  debtLimit: 0,
  brand: "",
};

export default function CustomerUpdateDialog({ item }) {
  const { handleOpenDialog, closeDialog } = useAppGlobal();
  const [searchSales, setSearchSales] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [brandList, setBrandList] = useState([brandTemp]);
  const [addressList, setAddressList] = useState([]);
  const [isOverBudget, setIsOverBudget] = useState(false);
  const debouncedSearchSales = useDebounce(searchSales, 500);
  const { trigger: updateCustomer } = useCustomerUpdate(item?._id);
  const { trigger: createCustomer } = useCustomerCreate();

  const {
    items: salesItems,
    isLoading: salesLoading,
    loadingMore: salesLoadingMore,
    hasMore: salesHasMore,
    onLoadMore: onLoadMoreSales,
  } = useInfiniteAutocomplete(
    "/api/v1/users",
    { limit: 10, type: userType.sales, searchField: "fullName", searchValue: debouncedSearchSales || undefined },
    true,
    debouncedSearchSales,
  );

  const form = useForm({
    resolver: yupResolver(schema),
    values: {
      fullName: item?.fullName ?? undefined,
      email: item?.email ?? undefined,
      phone: item?.phone ?? undefined,
      debtLimit: item?.debtLimit,
      type: item?.type ?? customerType.individualCustomer,
      companyName: item?.companyName ?? undefined,
      taxCode: item?.taxCode ?? undefined,
      sale:
        {
          _id: item?.sales?._id,
          value: item?.sales?._id,
          label: item?.sales?.fullName,
        } ?? null,
    },
  });
  const type = form.watch("type");
  const debtLimit = form.watch("debtLimit");

  const customerDetail = item;

  const resetAutocompleteState = () => {
    setSearchSales("");
    setAutocompleteKey((prev) => prev + 1);
  };

  const handleClose = () => {
    resetAutocompleteState();
    closeDialog();
  };

  const handleInputChangeSales = (_, value, reason) => {
    if (reason === "reset") return;
    if (reason === "clear") {
      setSearchSales("");
      return;
    }
    setSearchSales(value);
  };

  const handleUpdateCustomer = async (values) => {
    if (!!item?._id) {
      const res = await updateCustomer({
        body: {
          fullName: values?.fullName,
          type: values?.type,
          email: values?.email,
          phone: values?.phone,
          debtLimit: Number(values?.debtLimit),
          sales: values?.sale?._id,
          companyName: values?.companyName,
          taxCode: values?.taxCode,
          customerBrands: brandList?.map((v) => ({ ...v, brand: v?._id })),
          customerAddresses: [],
        },
      });
      if (res?.success) {
        handleClose();
        return enqueueSnackbar("Thành công", { variant: "success" });
      } else {
        return enqueueSnackbar(res?.error?.message ?? "Có lỗi xảy ra", { variant: "error" });
      }
    } else {
      const res = await createCustomer({
        body: {
          fullName: values?.fullName,
          type: values?.type,
          email: values?.email,
          phone: values?.phone,
          debtLimit: Number(values?.debtLimit),
          sales: values?.sale?._id,
          companyName: values?.companyName,
          taxCode: values?.taxCode,
          customerBrands: brandList?.map((v) => ({ ...v, brand: v?._id })),
          customerAddresses: addressList?.map((v) => ({
            recipientName: v?.recipientName,
            detailAddress: v?.detailAddress,
            phone: v?.phone,
            isDefault: v?.isDefault ? true : false,
            ward: v?.ward,
            country: v?.country,
          })),
        },
      });
      if (res?.success) {
        handleClose();
        return enqueueSnackbar("Thành công", { variant: "success" });
      } else {
        return enqueueSnackbar(res?.error?.message ?? "Có lỗi xảy ra", { variant: "error" });
      }
    }
  };

  const salesOptions = useMemo(() => {
    return salesItems.map((v) => ({
      _id: v?._id,
      value: v?._id,
      label: v?.fullName,
    }));
  }, [salesItems]);

  return (
    <FormProvider {...form}>
      <DialogContent>
        <Grid container>
          <Grid
            item
            xs={12}
            mb={2}
          >
            <Controller
              name="type"
              control={form.control}
              render={({ field }) => {
                return (
                  <RadioGroup
                    value={field.value}
                    row
                    onChange={field.onChange}
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                  >
                    <FormControlLabel
                      value={customerType.individualCustomer}
                      control={<Radio />}
                      label="KH cá nhân"
                    />
                    <FormControlLabel
                      value={customerType.business}
                      control={<Radio />}
                      label="Doanh nghiệp"
                    />
                  </RadioGroup>
                );
              }}
            />
          </Grid>
          <CustomerInput
            name="fullName"
            label="Tên đầy đủ"
            required
          />
          <CustomerUpdateAutocomplete
            key={`sale-${autocompleteKey}`}
            name="sale"
            label="NV phụ trách"
            options={salesOptions}
            disablePortal
            mt="24px"
            required
            loading={salesLoading}
            hasMore={salesHasMore}
            loadingMore={salesLoadingMore}
            onLoadMore={onLoadMoreSales}
            onInputChange={handleInputChangeSales}
          />
          <CustomerInput
            name="companyName"
            label="Tên công ty"
            mt="24px"
            required={type === customerType.business}
          />
          <CustomerInput
            name="taxCode"
            label="Mã số thuế"
            mt="24px"
            required={type === customerType.business}
          />
          <Grid
            item
            xs={12}
            mt="24px"
          >
            <PhoneNumberInput
              name="phone"
              label="Số điện thoại"
              width="100%"
              control={form.control}
              required
            />
          </Grid>
          <CustomerInput
            name="email"
            label="Email"
            mt="24px"
            required
          />
          <Grid
            item
            xs={12}
            mt="24px"
          >
            <Controller
              name="debtLimit"
              control={form.control}
              render={({ field }) => {
                return (
                  <NumericFormat
                    fullWidth
                    // defaultValue={item?.debtLimit}
                    InputProps={{
                      inputProps: { min: 0 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>đ{debtLimit ? "" : 0}</Typography>
                        </InputAdornment>
                      ),
                    }}
                    label={
                      <span>
                        Giới hạn tổng công nợ
                        <span style={{ color: "red", marginLeft: 2 }}>*</span>
                      </span>
                    }
                    thousandSeparator
                    customInput={TextField}
                    value={field.value}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue);
                    }}
                  />
                );
              }}
            />
            {customerDetail?.debtLimit > 0 && (
              <Typography
                mt="6px"
                sx={{ fontStyle: "italic" }}
              >
                <strong>({convertCurrency(customerDetail?.debtLimit) ?? "N/A"} đồng)</strong>
              </Typography>
            )}
          </Grid>
          <Divider sx={{ borderColor: "var(--grey) !important", width: "100%", mt: "24px" }} />
          {/* ----- ADDRESS */}
          <CustomerUpdateAddress
            item={item}
            onOpenDialog={handleOpenDialog}
            onCloseDialog={handleClose}
            addressList={addressList}
            setAddressList={setAddressList}
          />
          <Divider sx={{ borderColor: "var(--grey) !important", width: "100%", m: "24px 0" }} />
          {/* ----- BRAND */}
          <BrandUpdate
            totalDebt={debtLimit}
            item={item}
            setBrandsList={setBrandList}
            brandsList={brandList}
            setIsOverBudget={setIsOverBudget}
          />
        </Grid>
      </DialogContent>
      <DialogActions sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start", p: "12px 24px 24px !important" }}>
        {isOverBudget && (
          <Typography
            variant="caption"
            color="error"
          >
            Không thể cập nhật do có giá trị vượt quá mức cho phép
          </Typography>
        )}

        <Button
          onClick={form.handleSubmit(handleUpdateCustomer)}
          fullWidth
          variant="contained"
          disabled={isOverBudget}
        >
          Xác nhận
        </Button>
      </DialogActions>
    </FormProvider>
  );
}
